import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from './firebase';
import { StripeConfig, StripeConfigService } from '../types';

/**
 * Simple encryption/decryption for sensitive data
 * In production, consider using a more robust encryption library
 */
class SimpleEncryption {
  private static readonly KEY = 'tfr-stripe-config-2024'; // In production, use environment variable

  static encrypt(text: string): string {
    try {
      // Simple XOR encryption (for demo purposes)
      // In production, use proper encryption like crypto-js
      let result = '';
      for (let i = 0; i < text.length; i++) {
        result += String.fromCharCode(
          text.charCodeAt(i) ^ this.KEY.charCodeAt(i % this.KEY.length)
        );
      }
      return btoa(result); // Base64 encode
    } catch (error) {
      console.error('Encryption error:', error);
      return text; // Fallback to plain text
    }
  }

  static decrypt(encryptedText: string): string {
    try {
      const decoded = atob(encryptedText); // Base64 decode
      let result = '';
      for (let i = 0; i < decoded.length; i++) {
        result += String.fromCharCode(
          decoded.charCodeAt(i) ^ this.KEY.charCodeAt(i % this.KEY.length)
        );
      }
      return result;
    } catch (error) {
      console.error('Decryption error:', error);
      return encryptedText; // Fallback to encrypted text
    }
  }
}

/**
 * StripeConfigService - Manages Stripe configuration with Firebase storage
 * Handles secure storage and retrieval of Stripe API keys
 */
class StripeConfigServiceImpl implements StripeConfigService {
  private static instance: StripeConfigServiceImpl;
  private readonly COLLECTION_NAME = 'stripe_config';
  private readonly DOCUMENT_ID = 'main_config';

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): StripeConfigServiceImpl {
    if (!StripeConfigServiceImpl.instance) {
      StripeConfigServiceImpl.instance = new StripeConfigServiceImpl();
    }
    return StripeConfigServiceImpl.instance;
  }

  /**
   * Get current Stripe configuration
   */
  public async getConfig(): Promise<StripeConfig | null> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, this.DOCUMENT_ID);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      
      // Decrypt sensitive fields
      const config: StripeConfig = {
        id: data.id,
        publishableKey: data.publishableKey,
        secretKey: this.decryptSecret(data.secretKey),
        webhookSecret: data.webhookSecret ? this.decryptSecret(data.webhookSecret) : undefined,
        environment: data.environment,
        isActive: data.isActive,
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
        updatedBy: data.updatedBy,
        createdAt: data.createdAt?.toDate() || new Date()
      };

      return config;
    } catch (error) {
      console.error('Error getting Stripe config:', error);
      throw new Error('Failed to retrieve Stripe configuration');
    }
  }

  /**
   * Update Stripe configuration
   */
  public async updateConfig(configUpdate: Partial<StripeConfig>): Promise<StripeConfig> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, this.DOCUMENT_ID);
      
      // Get existing config
      const existingConfig = await this.getConfig();
      
      // Prepare update data
      const updateData: any = {
        ...configUpdate,
        lastUpdated: serverTimestamp(),
        updatedBy: configUpdate.updatedBy || 'admin'
      };

      // Encrypt sensitive fields if provided
      if (configUpdate.secretKey) {
        updateData.secretKey = this.encryptSecret(configUpdate.secretKey);
      }
      if (configUpdate.webhookSecret) {
        updateData.webhookSecret = this.encryptSecret(configUpdate.webhookSecret);
      }

      if (existingConfig) {
        // Update existing document
        await updateDoc(docRef, updateData);
      } else {
        // Create new document
        const newConfig: any = {
          id: this.DOCUMENT_ID,
          publishableKey: configUpdate.publishableKey || '',
          secretKey: configUpdate.secretKey ? this.encryptSecret(configUpdate.secretKey) : '',
          webhookSecret: configUpdate.webhookSecret ? this.encryptSecret(configUpdate.webhookSecret) : '',
          environment: configUpdate.environment || 'test',
          isActive: configUpdate.isActive !== undefined ? configUpdate.isActive : true,
          createdAt: serverTimestamp(),
          ...updateData
        };
        
        await setDoc(docRef, newConfig);
      }

      // Return updated config
      const updatedConfig = await this.getConfig();
      if (!updatedConfig) {
        throw new Error('Failed to retrieve updated configuration');
      }

      return updatedConfig;
    } catch (error) {
      console.error('Error updating Stripe config:', error);
      throw new Error('Failed to update Stripe configuration');
    }
  }

  /**
   * Test Stripe connection with provided configuration
   */
  public async testConnection(config: Partial<StripeConfig>): Promise<boolean> {
    try {
      // Import Stripe dynamically to avoid issues if not configured
      const { loadStripe } = await import('@stripe/stripe-js');
      
      if (!config.publishableKey) {
        return false;
      }

      // Test publishable key by creating a Stripe instance
      const stripe = await loadStripe(config.publishableKey);
      
      if (!stripe) {
        return false;
      }

      // Test if we can create payment elements (this validates the key)
      try {
        // This will throw an error if the key is invalid
        await stripe.elements();
        return true;
      } catch (error) {
        console.error('Stripe key validation failed:', error);
        return false;
      }
    } catch (error) {
      console.error('Error testing Stripe connection:', error);
      return false;
    }
  }

  /**
   * Encrypt secret key
   */
  public encryptSecret(secret: string): string {
    return SimpleEncryption.encrypt(secret);
  }

  /**
   * Decrypt secret key
   */
  public decryptSecret(encryptedSecret: string): string {
    return SimpleEncryption.decrypt(encryptedSecret);
  }

  /**
   * Get configuration status
   */
  public async getConfigStatus(): Promise<{
    isConfigured: boolean;
    isActive: boolean;
    environment: 'test' | 'live' | null;
    lastUpdated: Date | null;
  }> {
    try {
      const config = await this.getConfig();
      
      if (!config) {
        return {
          isConfigured: false,
          isActive: false,
          environment: null,
          lastUpdated: null
        };
      }

      return {
        isConfigured: !!(config.publishableKey && config.secretKey),
        isActive: config.isActive,
        environment: config.environment,
        lastUpdated: config.lastUpdated
      };
    } catch (error) {
      console.error('Error getting config status:', error);
      return {
        isConfigured: false,
        isActive: false,
        environment: null,
        lastUpdated: null
      };
    }
  }

  /**
   * Validate configuration data
   */
  public validateConfig(config: Partial<StripeConfig>): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (config.publishableKey) {
      if (!config.publishableKey.startsWith('pk_')) {
        errors.push('Publishable key must start with "pk_"');
      }
      if (config.publishableKey.length < 50) {
        errors.push('Publishable key appears to be too short');
      }
    }

    if (config.secretKey) {
      if (!config.secretKey.startsWith('sk_')) {
        errors.push('Secret key must start with "sk_"');
      }
      if (config.secretKey.length < 50) {
        errors.push('Secret key appears to be too short');
      }
    }

    if (config.environment && !['test', 'live'].includes(config.environment)) {
      errors.push('Environment must be either "test" or "live"');
    }

    if (config.webhookSecret && !config.webhookSecret.startsWith('whsec_')) {
      errors.push('Webhook secret must start with "whsec_"');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// Export singleton instance
export const stripeConfigService = StripeConfigServiceImpl.getInstance();

// Export class for testing
export { StripeConfigServiceImpl };
